"use client";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { HistoryRange } from "@/lib/app-detail/daily-buckets";
import type { PriceHistoryResult } from "@/lib/app-detail/price-history";

const EMPTY: PriceHistoryResult = {
  segments: [],
  changes: [],
  meta: { currency: "USD", currencyAssumed: true, iap: null, fetches: 0, firstAt: null, latestAt: null, free: false },
};

/** Every price this app has charged in one storefront. The card and the header pill share this query. */
export function useAppPriceHistory(appId: number, country: string, range: HistoryRange) {
  const cc = (country || "us").toLowerCase();
  const { data, isPending, isFetching, error } = useQuery<PriceHistoryResult, Error>({
    queryKey: ["app-price-history", appId, cc, range],
    enabled: !!appId,
    placeholderData: keepPreviousData,
    // Sensor Tower fetches land a few times a day at most, and irregularly.
    staleTime: 12 * 60 * 60 * 1000,
    queryFn: async () => {
      const res = await fetch(`/api/app-detail/price-history?appId=${appId}&country=${cc}&range=${range}`);
      if (!res.ok) throw new Error(`Failed to fetch price history: ${res.statusText}`);
      return (await res.json()) as PriceHistoryResult;
    },
  });
  return { data: data ?? EMPTY, isLoading: isPending, isFetching, error: error?.message ?? null };
}
